import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import userSettingsService from '../services/userSettings.service';

const SETTINGS_KEY = 'global_filters';
const SAVE_DEBOUNCE_MS = 500;

interface GlobalFiltersState {
  selectedRegions: string[];
  selectedOrderDispatchers: string[];
  selectedDriverDispatchers: string[];
}

interface GlobalFiltersContextType extends GlobalFiltersState {
  setSelectedRegions: (regions: string[]) => void;
  setSelectedOrderDispatchers: (dispatchers: string[]) => void;
  setSelectedDriverDispatchers: (dispatchers: string[]) => void;
  isLoading: boolean;
}

const defaultState: GlobalFiltersState = {
  selectedRegions: [],
  selectedOrderDispatchers: [],
  selectedDriverDispatchers: [],
};

const GlobalFiltersContext = createContext<GlobalFiltersContextType | undefined>(undefined);

export const GlobalFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedRegions, setSelectedRegionsState] = useState<string[]>(() => {
    const saved = localStorage.getItem('globalFilters_regions');
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedOrderDispatchers, setSelectedOrderDispatchersState] = useState<string[]>(() => {
    const saved = localStorage.getItem('globalFilters_orderDispatchers');
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedDriverDispatchers, setSelectedDriverDispatchersState] = useState<string[]>(() => {
    const saved = localStorage.getItem('globalFilters_driverDispatchers');
    return saved ? JSON.parse(saved) : [];
  });

  const [isLoading, setIsLoading] = useState(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load from user settings on mount
  useEffect(() => {
    // Abort any previous request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const loadFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting(SETTINGS_KEY, controller.signal);
        if (setting && setting.setting_value) {
          const filters = typeof setting.setting_value === 'string'
            ? JSON.parse(setting.setting_value)
            : setting.setting_value;

          if (filters.selectedRegions !== undefined) {
            setSelectedRegionsState(filters.selectedRegions);
            localStorage.setItem('globalFilters_regions', JSON.stringify(filters.selectedRegions));
          }
          if (filters.selectedOrderDispatchers !== undefined) {
            setSelectedOrderDispatchersState(filters.selectedOrderDispatchers);
            localStorage.setItem('globalFilters_orderDispatchers', JSON.stringify(filters.selectedOrderDispatchers));
          }
          if (filters.selectedDriverDispatchers !== undefined) {
            setSelectedDriverDispatchersState(filters.selectedDriverDispatchers);
            localStorage.setItem('globalFilters_driverDispatchers', JSON.stringify(filters.selectedDriverDispatchers));
          }
        }
      } catch (error: any) {
        // Ignore abort errors - expected when we cancel stale requests
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return;
        }
        // Silently fail - use localStorage values
        console.error('Failed to load global filters:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFilters();

    // Cleanup: abort any in-flight request on unmount
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Save to user settings (debounced)
  const saveFilters = useCallback((filters: GlobalFiltersState) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting(SETTINGS_KEY, filters);
      } catch (error) {
        console.error('Failed to save global filters:', error);
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const setSelectedRegions = useCallback((regions: string[]) => {
    setSelectedRegionsState(regions);
    localStorage.setItem('globalFilters_regions', JSON.stringify(regions));

    // Get current values for other filters to save complete state
    const currentOrderDispatchers = JSON.parse(localStorage.getItem('globalFilters_orderDispatchers') || '[]');
    const currentDriverDispatchers = JSON.parse(localStorage.getItem('globalFilters_driverDispatchers') || '[]');

    saveFilters({
      selectedRegions: regions,
      selectedOrderDispatchers: currentOrderDispatchers,
      selectedDriverDispatchers: currentDriverDispatchers,
    });
  }, [saveFilters]);

  const setSelectedOrderDispatchers = useCallback((dispatchers: string[]) => {
    setSelectedOrderDispatchersState(dispatchers);
    localStorage.setItem('globalFilters_orderDispatchers', JSON.stringify(dispatchers));

    const currentRegions = JSON.parse(localStorage.getItem('globalFilters_regions') || '[]');
    const currentDriverDispatchers = JSON.parse(localStorage.getItem('globalFilters_driverDispatchers') || '[]');

    saveFilters({
      selectedRegions: currentRegions,
      selectedOrderDispatchers: dispatchers,
      selectedDriverDispatchers: currentDriverDispatchers,
    });
  }, [saveFilters]);

  const setSelectedDriverDispatchers = useCallback((dispatchers: string[]) => {
    setSelectedDriverDispatchersState(dispatchers);
    localStorage.setItem('globalFilters_driverDispatchers', JSON.stringify(dispatchers));

    const currentRegions = JSON.parse(localStorage.getItem('globalFilters_regions') || '[]');
    const currentOrderDispatchers = JSON.parse(localStorage.getItem('globalFilters_orderDispatchers') || '[]');

    saveFilters({
      selectedRegions: currentRegions,
      selectedOrderDispatchers: currentOrderDispatchers,
      selectedDriverDispatchers: dispatchers,
    });
  }, [saveFilters]);

  return (
    <GlobalFiltersContext.Provider
      value={{
        selectedRegions,
        selectedOrderDispatchers,
        selectedDriverDispatchers,
        setSelectedRegions,
        setSelectedOrderDispatchers,
        setSelectedDriverDispatchers,
        isLoading,
      }}
    >
      {children}
    </GlobalFiltersContext.Provider>
  );
};

export const useGlobalFilters = (): GlobalFiltersContextType => {
  const context = useContext(GlobalFiltersContext);
  if (context === undefined) {
    throw new Error('useGlobalFilters must be used within a GlobalFiltersProvider');
  }
  return context;
};

/** Lenient lookup for components that also render outside the provider (tests,
 *  isolated previews): undefined there rather than a throw. */
export const useTryGlobalFilters = (): GlobalFiltersContextType | undefined => {
  return useContext(GlobalFiltersContext);
};

export default GlobalFiltersContext;
